'use client';

import {
  createContext,
  useContext,
  useRef,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { Popover } from '@base-ui/react/popover';
import cn from 'classnames';
import { CheckIcon, XIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'next-i18next';

import usePrefersReducedMotion from '@hooks/usePrefersReducedMotion';
import type { SizeOption } from '@adapters/bigcommerce/hooks/product/variantAvailability';
import SelectionCell from '_components/UI/SelectionCell';
import SimpleLink from '_components/UI/SimpleLink';
import LoadingSpinner from '@components/ui/LoadingSpinner';

import QuickAddTrigger from '../QuickAddTrigger';
import styles from './QuickAddPopover.module.scss';

/** CTA height — the desktop panel's bottom edge aligns with the trigger's. */
const TRIGGER_HEIGHT = 48;
const SKELETON_CELL_COUNT = 8;

/**
 * Compound Quick Add popover (Figma "Add to cart Popover", node 372:4915;
 * desktop uses the Application=Product Card variants, e.g. 746:239031).
 *
 * `Root`/`Trigger`/`Content` wrap the Base UI popover machinery (desktop:
 * panel anchored over the card image bottom; mobile: viewport-bottom sheet
 * with backdrop). The body parts — `Header`, `SizeGrid`, `Skeleton`,
 * `Spinner`, `Success`, `Fallback` — render standalone in Storybook/specs:
 * layout-sensitive parts read `QuickAddPopoverContext` when inside `Root`
 * and default to the desktop layout otherwise. They compose existing UI
 * subcomponents (`SelectionCell`, `SimpleLink`, `TextButton`/`IconButton`
 * via the trigger).
 *
 * ```tsx
 * <QuickAddPopover.Root open={open} onOpenChange={setOpen} isMobileLayout={isMobileLayout}>
 *   <QuickAddPopover.Trigger label="Quick Add to Cart" aria-label="…" />
 *   <QuickAddPopover.Content aria-labelledby={titleId}>
 *     <QuickAddPopover.Header titleId={titleId} onClose={close} />
 *     <QuickAddPopover.SizeGrid sizes={sizes} onSizeSelect={add} />
 *   </QuickAddPopover.Content>
 * </QuickAddPopover.Root>
 * ```
 */

export type QuickAddPopoverContextValue = {
  isMobileLayout: boolean;
};

/** Exported so stories/specs can force a layout without mounting `Root`. */
export const QuickAddPopoverContext =
  createContext<QuickAddPopoverContextValue | null>(null);

function useQuickAddPopoverContext() {
  const ctx = useContext(QuickAddPopoverContext);
  if (!ctx) {
    throw new Error(
      'QuickAddPopover components must be used within QuickAddPopover.Root'
    );
  }
  return ctx;
}

/** Body-part layout: the sheet inside a mobile `Root`, desktop otherwise
    (including standalone renders in stories/specs). */
function useQuickAddPopoverLayout() {
  return useContext(QuickAddPopoverContext)?.isMobileLayout ?? false;
}

export type QuickAddPopoverRootProps = {
  children: ReactNode;
  open?: boolean;
  /** Base UI's own `onOpenChange` — receives `(open, eventDetails)` so the
      caller can inspect `eventDetails.reason` and `.cancel()` unwanted
      dismissals (e.g. an outside press while an add is in flight). */
  onOpenChange?: ComponentProps<typeof Popover.Root>['onOpenChange'];
  /** Owned by the caller (`_components/QuickAdd/index.tsx`) rather than
      computed here — the orchestrator already needs this same breakpoint
      for its own anchor/pointer-leave/scroll-lock logic, and every card on
      a PLP grid mounts a `Root`, so a second independent `useBreakpoint`
      call here would double up on resize listeners across the grid and
      risked drifting out of sync with the caller's own value. */
  isMobileLayout: boolean;
};

function QuickAddPopoverRoot({
  children,
  open,
  onOpenChange,
  isMobileLayout,
}: QuickAddPopoverRootProps) {
  return (
    <QuickAddPopoverContext.Provider value={{ isMobileLayout }}>
      <Popover.Root
        open={open}
        onOpenChange={onOpenChange}
        modal={isMobileLayout}
      >
        {children}
      </Popover.Root>
    </QuickAddPopoverContext.Provider>
  );
}

export type QuickAddPopoverTriggerProps = {
  /** Visible CTA label (desktop face). */
  label: string;
  'aria-label': string;
  /**
   * Exposes the trigger's DOM node to the caller — Base UI restores focus
   * to it on Escape/outside-press, but a close driven from outside that
   * machinery (e.g. a `setTimeout`, with no originating DOM event for Base
   * UI to key off of) doesn't get that for free, so the caller focuses it
   * explicitly in those cases.
   */
  triggerRef?: React.Ref<HTMLButtonElement>;
};

function QuickAddPopoverTrigger({
  label,
  'aria-label': ariaLabel,
  triggerRef,
}: QuickAddPopoverTriggerProps) {
  const { isMobileLayout } = useQuickAddPopoverContext();

  return (
    <Popover.Trigger
      render={(triggerProps) => {
        // Strip Base UI's native `type` so it can't collide with the
        // TextButton/IconButton visual `type` prop.
        const { type: _type, ...rest } =
          triggerProps as React.ButtonHTMLAttributes<HTMLButtonElement>;
        return (
          <QuickAddTrigger
            {...rest}
            ref={triggerRef}
            isMobileLayout={isMobileLayout}
            label={label}
            aria-label={ariaLabel}
          />
        );
      }}
    />
  );
}

export type QuickAddPopoverContentProps = {
  children: ReactNode;
  /** `success` collapses the panel to the 56px confirmation bar. */
  variant?: 'panel' | 'success';
  'aria-labelledby'?: string;
  /** Desktop: bounds `size()`'s available space to the card image instead
      of the viewport — drives `--available-width` (`.positionerDesktop`)
      and is the pre-measurement fallback for `availableHeight` below. */
  collisionBoundary?: Element | null;
  /** Desktop: the measured room above the anchor within the card, exposed
      as `--card-available-height` for the panel's max-height cap (Figma's
      400px limit) — a static value, unlike floating-ui's viewport-relative
      `--available-height`, so it doesn't shift as the page scrolls. */
  availableHeight?: number | null;
  /** Desktop: a stable positioning reference, separate from the actual
      trigger button — the trigger animates itself out of view once open,
      and Base UI would otherwise track that shifting rect and drag the
      panel down with it. Undefined on mobile (fixed sheet, no anchoring). */
  anchor?: React.RefObject<Element | null>;
  /** Popover open state. Desktop only: when it flips to `false` the surface
      slides down (Figma step 6). Mobile slides the whole sheet via Base UI. */
  open?: boolean;
};

function QuickAddPopoverContent({
  children,
  variant = 'panel',
  'aria-labelledby': ariaLabelledBy,
  collisionBoundary,
  anchor,
  availableHeight,
  open,
}: QuickAddPopoverContentProps) {
  const { isMobileLayout } = useQuickAddPopoverContext();
  const prefersReducedMotion = usePrefersReducedMotion();

  // Desktop close: slide the surface down (see the motion.div below). Mobile
  // slides the whole sheet via Base UI, so it never drives the surface here.
  const isClosing = open === false && !isMobileLayout;

  // The mobile confirmation bar (Figma 801:82290) is a different shape from
  // the flush size sheet: it floats inset from the viewport edges with its
  // own drop shadow, and the backdrop fades away behind it.
  const isMobileSuccess = isMobileLayout && variant === 'success';

  const surfaceClassName = cn(
    styles.surface,
    isMobileLayout ? styles.surfaceSheet : styles.surfaceDesktop,
    variant === 'success' ? styles.surfaceSuccess : styles.surfacePanel
  );

  // Mobile: pin the sheet/toast to the viewport bottom via INLINE styles, not
  // just the `.positionerSheet` / `.positionerSheetSuccess` classes. On mobile
  // we hand Base UI no anchor, so floating-ui falls back to anchoring the popup
  // over the card's trigger button — mid-viewport. The class-based `!important`
  // overrides are what normally pull it to the bottom, but when that stylesheet
  // is momentarily out of sync with the DOM (a hashed CSS-module class in
  // cached JS/HTML that no longer matches the served CSS chunk — seen on the
  // frequently-redeployed preview), the override silently drops and the sheet
  // strands at floating-ui's mid-screen anchor. Inline styles ship in the JS
  // bundle alongside this JSX so they can't fall out of sync, and Base UI
  // merges the caller `style` *after* floating-ui's own inline positioning, so
  // these win over it without needing `!important`. The classes stay as a
  // matched belt-and-suspenders (their `!important` values mirror these); if
  // the CSS does load it wins with the identical result. `dvh`/`env()` are
  // expressed as strings since React inline styles can't carry units otherwise.
  const positionerStyle: React.CSSProperties = !isMobileLayout
    ? availableHeight != null
      ? ({
          '--card-available-height': `${availableHeight}px`,
        } as React.CSSProperties)
      : {}
    : isMobileSuccess
    ? {
        position: 'fixed',
        top: 'auto',
        transform: 'none',
        opacity: 1,
        // Mirrors `.positionerSheetSuccess`: floats inset from the edges (16px,
        // or the safe-area inset when deeper) as a toast once the backdrop goes.
        bottom: 'max(var(--component-spacing-16), env(safe-area-inset-bottom))',
        left: 'max(var(--component-spacing-16), env(safe-area-inset-left))',
        right: 'max(var(--component-spacing-16), env(safe-area-inset-right))',
        width: 'auto',
        // Figma (801:82290) caps the toast at 315px and centres it, rather than
        // stretching edge-to-edge like the size sheet. `margin-inline: auto`
        // centres it within the left/right insets; on phones narrower than
        // 315px + insets it shrinks to fit instead of overflowing.
        maxWidth: '315px',
        marginInline: 'auto',
      }
    : {
        // Mirrors `.positionerSheet`: the flush, edge-to-edge size sheet.
        position: 'fixed',
        top: 'auto',
        bottom: 0,
        left: 0,
        right: 0,
        transform: 'none',
        width: '100%',
        maxWidth: 'none',
        opacity: 1,
      };

  return (
    <Popover.Portal>
      {isMobileLayout && (
        <Popover.Backdrop
          className={cn(
            styles.backdrop,
            isMobileSuccess && styles.backdropHidden
          )}
        />
      )}
      <Popover.Positioner
        side="top"
        align="center"
        anchor={isMobileLayout ? undefined : anchor}
        sideOffset={isMobileLayout ? 0 : -TRIGGER_HEIGHT}
        collisionBoundary={
          isMobileLayout ? undefined : collisionBoundary ?? undefined
        }
        // Desktop: the panel must stay pinned over the card's image, never
        // flip to the opposite side or shift along the trigger's axis as
        // scroll changes the room above it (Figma has no "flipped"
        // variant). It still shrinks via `--card-available-height` below,
        // with `.surfacePanel`'s internal scroll handling overflow.
        collisionAvoidance={
          isMobileLayout ? undefined : { side: 'none', align: 'none' }
        }
        className={cn(
          styles.positioner,
          isMobileLayout ? styles.positionerSheet : styles.positionerDesktop,
          isMobileSuccess && styles.positionerSheetSuccess
        )}
        style={positionerStyle}
      >
        <Popover.Popup
          aria-labelledby={ariaLabelledBy}
          className={cn(
            styles.popup,
            isMobileLayout ? styles.popupSheet : styles.popupDesktop,
            isMobileSuccess && styles.popupSheetSuccess
          )}
        >
          {/* Panel → success (Figma step 4): the outgoing surface slides down
              through the popup's clipped bottom edge while the new one slides
              up into place. Spring 80/20 ≈ duration-bound bounce-0 spring over
              400ms.

              Desktop close (Figma step 6): the same surface slides down by
              animating `y` to 100% — clipped by the popup's `overflow: clip`,
              so it disappears at the image edge rather than overshooting like a
              transform on the (portaled, unclipped) popup would. The child
              stays mounted so its grid cell never collapses — a removal would
              make Base UI's positioner re-measure and re-anchor. The popup
              itself just fades its drop-shadow out (SCSS `[data-ending-style]`),
              staying mounted long enough for this slide. Mobile keeps its Base
              UI sheet slide (`isClosing` is false there). */}
          {prefersReducedMotion ? (
            <div className={surfaceClassName}>{children}</div>
          ) : (
            <AnimatePresence initial={false}>
              <motion.div
                key={variant}
                className={surfaceClassName}
                initial={{ y: '100%' }}
                animate={{ y: isClosing ? '100%' : 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', duration: 0.4, bounce: 0 }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          )}
        </Popover.Popup>
      </Popover.Positioner>
    </Popover.Portal>
  );
}

export type QuickAddPopoverHeaderProps = {
  /** id for the "Select Size" heading (popup `aria-labelledby` target). */
  titleId?: string;
  onClose: () => void;
  /** Overrides the default "Select Size" title. */
  children?: ReactNode;
  /**
   * Keeps the title as the popup's accessible name without showing it —
   * for views (e.g. the spinner) where there's nothing left to select.
   */
  visuallyHidden?: boolean;
};

/**
 * Desktop (Figma 746:239031): centered "Select Size" (14/18 medium), no
 * close affordance — outside click/Esc dismisses. Sheet (Figma 801:81760):
 * left-aligned title (18/20 semibold) + 24px X close.
 */
function QuickAddPopoverHeader({
  titleId,
  onClose,
  children,
  visuallyHidden = false,
}: QuickAddPopoverHeaderProps) {
  const { t } = useTranslation('plp');
  const isMobileLayout = useQuickAddPopoverLayout();

  return (
    <div
      className={cn(
        styles.header,
        isMobileLayout ? styles.headerSheet : styles.headerDesktop
      )}
    >
      <span
        id={titleId}
        className={cn(
          isMobileLayout ? styles.titleSheet : styles.titleDesktop,
          visuallyHidden && styles.titleHidden
        )}
      >
        {children ?? t('quickAdd.selectSize')}
      </span>
      {isMobileLayout ? (
        <button
          type="button"
          className={styles.closeButton}
          aria-label={t('quickAdd.close')}
          onClick={onClose}
        >
          <XIcon size={24} aria-hidden weight="regular" />
        </button>
      ) : null}
    </div>
  );
}

export type QuickAddPopoverSizeGridProps = {
  sizes: SizeOption[];
  selectedSizeId?: number | null;
  onSizeSelect?: (size: SizeOption) => void;
};

const GRID_ARROW_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
]);

/**
 * Desktop (Figma 746:239031): equal-width cells in wrapping rows.
 * Sheet (Figma 801:81760): four equal columns. In both, a partial last
 * row keeps the cell width instead of stretching to fill.
 */
function QuickAddPopoverSizeGrid({
  sizes,
  selectedSizeId = null,
  onSizeSelect,
}: QuickAddPopoverSizeGridProps) {
  const isMobileLayout = useQuickAddPopoverLayout();
  const gridRef = useRef<HTMLDivElement>(null);

  // Desktop's column count is dynamic (`auto-fill`), so true 2D row/column
  // math isn't reliable — Left/Up and Right/Down instead step through the
  // enabled pills in visual (DOM) order, wrapping at the ends. This still
  // satisfies "arrow keys move between pills" without hardcoding a column
  // count that would drift from the actual rendered layout.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!GRID_ARROW_KEYS.has(event.key) || !gridRef.current) {
      return;
    }

    const cells = Array.from(
      gridRef.current.querySelectorAll<HTMLButtonElement>(
        'button:not(:disabled)'
      )
    );
    const currentIndex = cells.indexOf(
      document.activeElement as HTMLButtonElement
    );
    if (currentIndex === -1) {
      return;
    }

    event.preventDefault();

    const isForward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const nextIndex =
      (currentIndex + (isForward ? 1 : -1) + cells.length) % cells.length;
    cells[nextIndex]?.focus();
  };

  return (
    <div
      ref={gridRef}
      onKeyDown={handleKeyDown}
      className={cn(
        styles.sizeGrid,
        isMobileLayout ? styles.sizeGridSheet : styles.sizeGridDesktop
      )}
    >
      {sizes
        .filter((size) => size.existsForSelectedColour)
        .map((size) => (
          <SelectionCell
            key={size.id}
            selected={size.id === selectedSizeId}
            disabled={
              !size.isAvailableForSelectedColour || !size.isPurchasableOnline
            }
            onClick={() => onSizeSelect?.(size)}
          >
            {size.name}
          </SelectionCell>
        ))}
    </div>
  );
}

export type QuickAddPopoverSkeletonProps = {
  /** Number of placeholder cells; defaults to {@link SKELETON_CELL_COUNT}
      when the caller has no variant-count hint. */
  cellCount?: number;
};

function QuickAddPopoverSkeleton({ cellCount }: QuickAddPopoverSkeletonProps) {
  const isMobileLayout = useQuickAddPopoverLayout();
  const count = cellCount && cellCount > 0 ? cellCount : SKELETON_CELL_COUNT;

  return (
    <div
      className={cn(
        styles.sizeGrid,
        isMobileLayout ? styles.sizeGridSheet : styles.sizeGridDesktop
      )}
      aria-busy="true"
      aria-hidden
    >
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className={styles.skeletonCell} />
      ))}
    </div>
  );
}

/** Centered spinner shown while a no-selection-needed add is in flight. */
function QuickAddPopoverSpinner() {
  return (
    <div className={styles.spinnerWrapper} aria-busy="true">
      <LoadingSpinner colour="var(--neutrals-1000)" />
    </div>
  );
}

export type QuickAddPopoverSuccessProps = {
  onClose: () => void;
  /** id for the "Added to Cart" label — the popup's `aria-labelledby`
      target while this view is showing (the `Header` title isn't rendered
      in this view, so the dialog would otherwise have no accessible name). */
  labelId?: string;
};

/** Green check + "Added to Cart" + X — the 56px confirmation bar. */
function QuickAddPopoverSuccess({
  onClose,
  labelId,
}: QuickAddPopoverSuccessProps) {
  const { t } = useTranslation('plp');

  return (
    <div className={styles.success} role="status" aria-live="assertive">
      <CheckIcon
        size={24}
        weight="regular"
        aria-hidden
        className={styles.successIcon}
      />
      <span id={labelId} className={styles.successLabel}>
        {t('quickAdd.added')}
      </span>
      <button
        type="button"
        className={styles.closeButton}
        aria-label={t('quickAdd.close')}
        onClick={onClose}
      >
        <XIcon size={24} aria-hidden weight="regular" />
      </button>
    </div>
  );
}

export type QuickAddPopoverFallbackProps = {
  /** Message shown above the "View product" link. */
  message?: 'oos' | 'error' | 'callToPurchase' | 'insufficientStock' | null;
  /**
   * Product-specific copy for `callToPurchase` — the same
   * `shippingRestrictions.message_en`/`message_fr` HTML the PDP renders for
   * a call-to-order-only item. Falls back to a generic message when the
   * product has no configured restriction copy.
   */
  restrictionMessage?: string | null;
  /** Requested/available counts for `insufficientStock` — interpolated into
      the same copy the cart page shows for a stock shortfall. */
  messageVariables?: { attemptedToAdd: number; added: number } | null;
  productUrl: string;
};

/** Terminal fallback: optional message + "View product" link to the PDP. */
function QuickAddPopoverFallback({
  message = null,
  restrictionMessage = null,
  messageVariables = null,
  productUrl,
}: QuickAddPopoverFallbackProps) {
  const { t } = useTranslation('plp');

  return (
    <div className={styles.fallback}>
      {message === 'callToPurchase' && restrictionMessage ? (
        <p
          className={styles.errorMessage}
          role="alert"
          dangerouslySetInnerHTML={{ __html: restrictionMessage }}
        />
      ) : message ? (
        <p className={styles.errorMessage} role="alert">
          {message === 'oos'
            ? t('quickAdd.oos')
            : message === 'callToPurchase'
            ? t('quickAdd.callToPurchase')
            : message === 'insufficientStock' && messageVariables
            ? // Mirrors the cart page's own copy for a stock shortfall
              // (`common:cart.message.insufficientQuantity`) rather than a
              // separate `plp`-namespace string, so Quick Add never
              // contradicts the message the shopper sees in-cart.
              t('common:cart.message.insufficientQuantity', messageVariables)
            : t('quickAdd.error.generic')}
        </p>
      ) : null}
      <SimpleLink
        href={productUrl}
        variant="secondary"
        size="small"
        className={styles.viewProductLink}
      >
        {t('quickAdd.viewProduct')}
      </SimpleLink>
    </div>
  );
}

const QuickAddPopover = {
  Root: QuickAddPopoverRoot,
  Trigger: QuickAddPopoverTrigger,
  Content: QuickAddPopoverContent,
  Header: QuickAddPopoverHeader,
  SizeGrid: QuickAddPopoverSizeGrid,
  Skeleton: QuickAddPopoverSkeleton,
  Spinner: QuickAddPopoverSpinner,
  Success: QuickAddPopoverSuccess,
  Fallback: QuickAddPopoverFallback,
} as const;

export default QuickAddPopover;

export {
  QuickAddPopoverRoot,
  QuickAddPopoverTrigger,
  QuickAddPopoverContent,
  QuickAddPopoverHeader,
  QuickAddPopoverSizeGrid,
  QuickAddPopoverSkeleton,
  QuickAddPopoverSpinner,
  QuickAddPopoverSuccess,
  QuickAddPopoverFallback,
};
